import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

// Mock user profiles for demo
const MOCK_PROFILES = [
  {
    id: 1,
    name: 'Ayşe',
    age: 28,
    avatar: '👩‍🦰',
    tagline: 'Kahve bağımlısı & kitap kurdu',
    interests: ['Kahve', 'Edebiyat', 'Yoga', 'Seyahat'],
    commonInterests: ['Kahve', 'Seyahat'],
    distance: '2.3 km',
    lastActive: '5 dk önce',
    favoriteBook: 'Simyacı - Paulo Coelho',
    favoriteCoffee: 'Flat White'
  },
  {
    id: 2,
    name: 'Mehmet',
    age: 32,
    avatar: '👨‍💼',
    tagline: 'Teknoloji & müzik tutkunu',
    interests: ['Teknoloji', 'Caz Müzik', 'Kahve', 'Fotoğrafçılık'],
    commonInterests: ['Kahve', 'Fotoğrafçılık'],
    distance: '1.8 km',
    lastActive: '12 dk önce',
    favoriteBook: 'Sapiens - Yuval Noah Harari',
    favoriteCoffee: 'V60 Pour Over'
  }
];

// AI Ice Breaker suggestions based on common interests
const generateIceBreakers = (profile) => {
  const suggestions = [];
  
  if (profile.commonInterests.includes('Kahve')) {
    suggestions.push(`"${profile.favoriteCoffee} sevenler kulübüne hoş geldin! En sevdiğin Espressolab şubesi hangisi?"`);
  }
  
  if (profile.commonInterests.includes('Seyahat')) {
    suggestions.push(`"Seyahat etmeyi seviyormuşsun! Son gittiğin yerde en iyi kahveyi nerede buldun?"`);
  }
  
  if (profile.commonInterests.includes('Fotoğrafçılık')) {
    suggestions.push(`"Fotoğrafçılık ilgin dikkatimi çekti! Kahve fotoğrafları çekmeyi denedin mi hiç?"`);
  }
  
  // Default suggestions based on coffee
  if (suggestions.length < 3) {
    suggestions.push(`"${profile.name}, ${profile.favoriteCoffee} hakkında ne düşünüyorsun? Ben de denemeyi düşünüyorum."`);
  }
  
  if (suggestions.length < 3) {
    suggestions.push(`"Espressolab'ın yeni sezon menüsünü denedin mi? Önerebileceğin bir şey var mı?"`);
  }
  
  if (suggestions.length < 3) {
    suggestions.push(`"Merhaba ${profile.name}! Kahve eşliğinde güzel bir sohbete ne dersin?"`);
  }
  
  return suggestions.slice(0, 3);
};

const EspressolabFinderDemo = () => {
  const navigate = useNavigate();
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [activeModal, setActiveModal] = useState(null); // 'gift', 'coffee', 'icebreaker', 'ortakfal'
  const [iceBreakers, setIceBreakers] = useState([]);

  const handleIceBreaker = (profile) => {
    const suggestions = generateIceBreakers(profile);
    setIceBreakers(suggestions);
    setSelectedProfile(profile);
    setActiveModal('icebreaker');
  };

  const ProfileCard = ({ profile }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(99, 102, 241, 0.05) 100%)',
        border: '1px solid rgba(139, 92, 246, 0.2)',
        borderRadius: '24px',
        padding: '24px',
        marginBottom: '20px'
      }}
    >
      {/* Profile Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
        <div style={{
          width: '72px',
          height: '72px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '36px',
          boxShadow: '0 8px 24px rgba(139, 92, 246, 0.3)'
        }}>
          {profile.avatar}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <h3 style={{ margin: 0, color: 'white', fontSize: '20px', fontWeight: '700' }}>
              {profile.name}, {profile.age}
            </h3>
            <span style={{
              background: 'rgba(34, 197, 94, 0.2)',
              color: '#22c55e',
              padding: '2px 8px',
              borderRadius: '10px',
              fontSize: '10px',
              fontWeight: '600'
            }}>
              ● Aktif
            </span>
          </div>
          <p style={{ margin: '4px 0 0', color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}>
            {profile.tagline}
          </p>
          <p style={{ margin: '4px 0 0', color: 'rgba(255,255,255,0.4)', fontSize: '11px' }}>
            📍 {profile.distance} • {profile.lastActive}
          </p>
        </div>
      </div>

      {/* Common Interests */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', marginBottom: '8px', fontWeight: '600' }}>
          ORTAK İLGİ ALANLARI
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {profile.commonInterests.map((interest, i) => (
            <span
              key={i}
              style={{
                background: 'rgba(236, 72, 153, 0.2)',
                color: '#f472b6',
                padding: '6px 12px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '500'
              }}
            >
              ✨ {interest}
            </span>
          ))}
          {profile.interests.filter(i => !profile.commonInterests.includes(i)).map((interest, i) => (
            <span
              key={i}
              style={{
                background: 'rgba(255,255,255,0.05)',
                color: 'rgba(255,255,255,0.5)',
                padding: '6px 12px',
                borderRadius: '20px',
                fontSize: '12px'
              }}
            >
              {interest}
            </span>
          ))}
        </div>
      </div>

      {/* Favorite Info */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '12px',
        marginBottom: '20px',
        padding: '12px',
        background: 'rgba(0,0,0,0.2)',
        borderRadius: '12px'
      }}>
        <div>
          <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '10px', marginBottom: '2px' }}>☕ Favori Kahve</div>
          <div style={{ color: 'white', fontSize: '13px', fontWeight: '500' }}>{profile.favoriteCoffee}</div>
        </div>
        <div>
          <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '10px', marginBottom: '2px' }}>📚 Favori Kitap</div>
          <div style={{ color: 'white', fontSize: '13px', fontWeight: '500' }}>{profile.favoriteBook.split(' - ')[0]}</div>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
        <button
          onClick={() => { setSelectedProfile(profile); setActiveModal('gift'); }}
          style={{
            padding: '14px',
            background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.2), rgba(139, 92, 246, 0.15))',
            border: '1px solid rgba(236, 72, 153, 0.3)',
            borderRadius: '14px',
            color: '#f472b6',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'all 0.2s'
          }}
        >
          ❤️ Fal Hediye Et
        </button>
        
        <button
          onClick={() => { setSelectedProfile(profile); setActiveModal('coffee'); }}
          style={{
            padding: '14px',
            background: 'linear-gradient(135deg, rgba(180, 83, 9, 0.2), rgba(217, 119, 6, 0.15))',
            border: '1px solid rgba(217, 119, 6, 0.3)',
            borderRadius: '14px',
            color: '#fbbf24',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'all 0.2s'
          }}
        >
          ☕ Kahve Ismarla
        </button>
        
        <button
          onClick={() => handleIceBreaker(profile)}
          style={{
            padding: '14px',
            background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.2), rgba(16, 185, 129, 0.15))',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            borderRadius: '14px',
            color: '#22c55e',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'all 0.2s'
          }}
        >
          ✨ AI Ice Breaker
        </button>
        
        <button
          onClick={() => { setSelectedProfile(profile); setActiveModal('ortakfal'); }}
          style={{
            padding: '14px',
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2), rgba(99, 102, 241, 0.15))',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            borderRadius: '14px',
            color: '#c084fc',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'all 0.2s'
          }}
        >
          🔮 Ortak Fal
        </button>
      </div>
    </motion.div>
  );

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0d0d2b 0%, #1a0533 50%, #0f172a 100%)',
      padding: '20px',
      paddingBottom: '100px'
    }}>
      {/* Concept Demo Badge */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.15), rgba(245, 158, 11, 0.1))',
          border: '1px solid rgba(251, 191, 36, 0.3)',
          borderRadius: '12px',
          padding: '12px 16px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}
      >
        <span style={{ fontSize: '20px' }}>⚠️</span>
        <div>
          <div style={{ color: '#fbbf24', fontWeight: '700', fontSize: '13px' }}>
            Konsept Demo
          </div>
          <div style={{ color: 'rgba(251, 191, 36, 0.8)', fontSize: '11px' }}>
            Gerçek Espressolab Finder API entegrasyonu değildir.
          </div>
        </div>
      </motion.div>

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '8px' }}>
          <span style={{ fontSize: '32px' }}>☕</span>
          <h1 style={{
            margin: 0,
            fontSize: '28px',
            fontWeight: '800',
            background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Espressolab Finder
          </h1>
        </div>
        <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', margin: 0 }}>
          Kahve tutkunlarıyla tanış, bağlan, keşfet
        </p>
      </div>

      {/* Profiles */}
      <div style={{ maxWidth: '500px', margin: '0 auto' }}>
        <div style={{ 
          color: 'rgba(255,255,255,0.4)', 
          fontSize: '12px', 
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span>📍</span>
          <span>Yakınındaki Espressolab severler</span>
        </div>
        
        {MOCK_PROFILES.map(profile => (
          <ProfileCard key={profile.id} profile={profile} />
        ))}
      </div>

      {/* Back Button */}
      <div style={{ textAlign: 'center', marginTop: '32px' }}>
        <button
          onClick={() => navigate('/')}
          style={{
            padding: '12px 24px',
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '12px',
            color: 'rgba(255,255,255,0.6)',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          ← Ana Sayfaya Dön
        </button>
      </div>

      {/* Modals */}
      <AnimatePresence>
        {activeModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
              zIndex: 1000
            }}
            onClick={() => setActiveModal(null)}
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              onClick={e => e.stopPropagation()}
              style={{
                background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                borderRadius: '24px',
                padding: '28px',
                width: '100%',
                maxWidth: '400px',
                maxHeight: '90vh',
                overflow: 'auto'
              }}
            >
              {/* Gift Modal */}
              {activeModal === 'gift' && selectedProfile && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>❤️</div>
                  <h3 style={{ margin: '0 0 8px', color: 'white', fontSize: '20px' }}>
                    Fal Hediye Et
                  </h3>
                  <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', marginBottom: '24px' }}>
                    {selectedProfile.name}'e mistik bir hediye gönder
                  </p>
                  
                  <div style={{
                    background: 'rgba(236, 72, 153, 0.1)',
                    border: '1px solid rgba(236, 72, 153, 0.2)',
                    borderRadius: '16px',
                    padding: '20px',
                    marginBottom: '20px'
                  }}>
                    <div style={{ fontSize: '24px', marginBottom: '8px' }}>🔮</div>
                    <div style={{ color: 'white', fontWeight: '600', marginBottom: '4px' }}>Kahve Falı</div>
                    <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>1 Kredi</div>
                  </div>
                  
                  <button
                    onClick={() => setActiveModal(null)}
                    style={{
                      width: '100%',
                      padding: '16px',
                      background: 'linear-gradient(135deg, #ec4899, #8b5cf6)',
                      border: 'none',
                      borderRadius: '14px',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '15px',
                      fontWeight: '600'
                    }}
                  >
                    Hediye Gönder
                  </button>
                </div>
              )}

              {/* Coffee Modal */}
              {activeModal === 'coffee' && selectedProfile && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>☕</div>
                  <h3 style={{ margin: '0 0 8px', color: 'white', fontSize: '20px' }}>
                    Kahve Ismarla
                  </h3>
                  <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', marginBottom: '24px' }}>
                    {selectedProfile.name}'e Espressolab'dan kahve ısmarla
                  </p>
                  
                  <div style={{
                    background: 'rgba(217, 119, 6, 0.1)',
                    border: '1px solid rgba(217, 119, 6, 0.2)',
                    borderRadius: '16px',
                    padding: '20px',
                    marginBottom: '16px'
                  }}>
                    <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', marginBottom: '8px' }}>
                      {selectedProfile.name}'in favorisi
                    </div>
                    <div style={{ color: 'white', fontWeight: '600', fontSize: '18px' }}>
                      {selectedProfile.favoriteCoffee}
                    </div>
                    <div style={{ color: '#fbbf24', fontSize: '14px', marginTop: '8px' }}>₺85</div>
                  </div>
                  
                  <div style={{
                    background: 'rgba(251, 191, 36, 0.1)',
                    borderRadius: '12px',
                    padding: '12px',
                    marginBottom: '20px',
                    fontSize: '12px',
                    color: '#fbbf24'
                  }}>
                    ⚠️ Bu bir konsept demosudur. Gerçek ödeme başlatılmayacaktır.
                  </div>
                  
                  <button
                    onClick={() => setActiveModal(null)}
                    style={{
                      width: '100%',
                      padding: '16px',
                      background: 'linear-gradient(135deg, #d97706, #b45309)',
                      border: 'none',
                      borderRadius: '14px',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '15px',
                      fontWeight: '600'
                    }}
                  >
                    Kahve Ismarla
                  </button>
                </div>
              )}

              {/* Ice Breaker Modal */}
              {activeModal === 'icebreaker' && selectedProfile && (
                <div>
                  <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>✨</div>
                    <h3 style={{ margin: '0 0 8px', color: 'white', fontSize: '20px' }}>
                      AI Ice Breaker
                    </h3>
                    <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px' }}>
                      {selectedProfile.name} ile sohbet başlatmak için öneriler
                    </p>
                  </div>
                  
                  <div style={{
                    background: 'rgba(34, 197, 94, 0.05)',
                    borderRadius: '12px',
                    padding: '12px',
                    marginBottom: '20px',
                    fontSize: '11px',
                    color: 'rgba(255,255,255,0.5)'
                  }}>
                    💡 Öneriler yalnızca profilde belirtilen ortak ilgi alanlarına ({selectedProfile.commonInterests.join(', ')}) dayanmaktadır.
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {iceBreakers.map((suggestion, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.1 }}
                        style={{
                          background: 'rgba(34, 197, 94, 0.1)',
                          border: '1px solid rgba(34, 197, 94, 0.2)',
                          borderRadius: '14px',
                          padding: '16px',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onClick={() => {
                          navigator.clipboard.writeText(suggestion.replace(/"/g, ''));
                          alert('Mesaj kopyalandı!');
                        }}
                      >
                        <div style={{ color: 'white', fontSize: '14px', lineHeight: '1.5' }}>
                          {suggestion}
                        </div>
                        <div style={{ color: 'rgba(34, 197, 94, 0.7)', fontSize: '11px', marginTop: '8px' }}>
                          Kopyalamak için tıkla
                        </div>
                      </motion.div>
                    ))}
                  </div>
                  
                  <button
                    onClick={() => setActiveModal(null)}
                    style={{
                      width: '100%',
                      marginTop: '20px',
                      padding: '14px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '12px',
                      color: 'rgba(255,255,255,0.6)',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Kapat
                  </button>
                </div>
              )}

              {/* Ortak Fal Modal */}
              {activeModal === 'ortakfal' && selectedProfile && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔮</div>
                  <h3 style={{ margin: '0 0 8px', color: 'white', fontSize: '20px' }}>
                    Ortak Fal
                  </h3>
                  <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', marginBottom: '24px' }}>
                    {selectedProfile.name} ile birlikte fal baktırın
                  </p>
                  
                  <div style={{
                    background: 'rgba(139, 92, 246, 0.1)',
                    border: '1px solid rgba(139, 92, 246, 0.2)',
                    borderRadius: '16px',
                    padding: '20px',
                    marginBottom: '20px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '20px', marginBottom: '16px' }}>
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '50%',
                        background: 'rgba(139, 92, 246, 0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '24px'
                      }}>
                        👤
                      </div>
                      <div style={{ fontSize: '24px', color: '#c084fc' }}>💫</div>
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '50%',
                        background: 'rgba(139, 92, 246, 0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '24px'
                      }}>
                        {selectedProfile.avatar}
                      </div>
                    </div>
                    <div style={{ color: 'white', fontWeight: '600' }}>İlişki Uyumu Falı</div>
                    <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', marginTop: '4px' }}>
                      İki kişinin enerjisini birleştiren özel yorum
                    </div>
                  </div>
                  
                  <div style={{
                    background: 'rgba(139, 92, 246, 0.1)',
                    borderRadius: '12px',
                    padding: '14px',
                    marginBottom: '20px',
                    textAlign: 'left'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <span style={{ fontSize: '16px' }}>ℹ️</span>
                      <span style={{ color: '#c084fc', fontWeight: '600', fontSize: '13px' }}>Nasıl Çalışır?</span>
                    </div>
                    <ul style={{ margin: 0, paddingLeft: '20px', color: 'rgba(255,255,255,0.6)', fontSize: '12px', lineHeight: '1.8' }}>
                      <li>İki kişinin de onayı gereklidir</li>
                      <li>Her iki taraf kahve fincanı fotoğrafı yükler</li>
                      <li>AI her iki fincanı analiz eder</li>
                      <li>Ortak yorum oluşturulur</li>
                    </ul>
                  </div>
                  
                  <div style={{
                    background: 'rgba(251, 191, 36, 0.1)',
                    borderRadius: '12px',
                    padding: '12px',
                    marginBottom: '20px',
                    fontSize: '12px',
                    color: '#fbbf24',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span>🎭</span>
                    <span>Bu özellik tamamen eğlence amaçlıdır.</span>
                  </div>
                  
                  <button
                    onClick={() => setActiveModal(null)}
                    style={{
                      width: '100%',
                      padding: '16px',
                      background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                      border: 'none',
                      borderRadius: '14px',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '15px',
                      fontWeight: '600'
                    }}
                  >
                    {selectedProfile.name}'e Davet Gönder
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EspressolabFinderDemo;
